<template>
  <div
    class="msc-faq-item"
    :class="{ 'msc-faq-item--open': isOpen }"
    role="button"
    tabindex="0"
    :aria-expanded="isOpen ? 'true' : 'false'"
    :aria-controls="collapseId"
    @click="toggle"
    @keydown.enter.prevent="toggle"
    @keydown.space.prevent="toggle"
  >
    <div class="msc-faq-item__question">
      <div class="msc-faq-item__mark msc-faq-item__mark--q" aria-hidden="true">
        Q<span class="msc-faq-item__dot"></span>
      </div>
      <div class="msc-faq-item__title">{{ faq.title }}</div>
      <div class="msc-faq-item__chevron" aria-hidden="true">
        <i class="now-ui-icons arrows-1_minimal-down"></i>
      </div>
    </div>

    <b-collapse :id="collapseId" v-model="isOpen" accordion="msc-help-faq" role="tabpanel">
      <div class="msc-faq-item__answer">
        <div class="msc-faq-item__mark msc-faq-item__mark--a" aria-hidden="true">
          A<span class="msc-faq-item__dot"></span>
        </div>
        <div class="msc-faq-item__body">
          <p class="msc-faq-item__text">{{ faq.description }}</p>
          <a
            v-if="faq.moreInformation"
            class="msc-faq-item__more"
            :href="faq.moreInformation"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            More information
          </a>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
  export default {
    name: 'FaqCard',
    props: {
      faq: {
        type: Object,
        default: () => ({}),
      },
      index: {
        type: Number,
        required: true,
      },
    },
    data() {
      return {
        isOpen: false,
      };
    },
    computed: {
      collapseId() {
        return `msc-faq-${this.index}`;
      },
    },
    methods: {
      toggle() {
        this.isOpen = !this.isOpen;
      },
    },
  };
</script>

<style scoped lang="scss">
  $msc-faq-q: #f96332;
  $msc-faq-a: #7fb847;
  $msc-faq-ink: #2f3a42;
  $msc-faq-body: #3d4a53;
  $msc-faq-muted: #5f6d78;
  $msc-faq-border: #e4eaef;

  .msc-faq-item {
    background: #fff;
    border: 1px solid $msc-faq-border;
    border-radius: 0.85rem;
    box-shadow: 0 3px 12px rgba(45, 62, 76, 0.06);
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover,
    &:focus {
      outline: none;
      border-color: rgba($msc-faq-q, 0.35);
      box-shadow: 0 5px 18px rgba(45, 62, 76, 0.1);
    }

    &--open {
      border-color: rgba($msc-faq-q, 0.4);
    }
  }

  .msc-faq-item__question {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 0.75rem 0.9rem;
    align-items: center;
    padding: 0.95rem 1rem 0.95rem 0.85rem;
    border-left: 4px solid $msc-faq-q;
  }

  .msc-faq-item__mark {
    position: relative;
    font-family: 'Open Sans Condensed', 'Montserrat', sans-serif;
    font-size: clamp(1.75rem, 4vw, 2.35rem);
    font-weight: 700;
    line-height: 0.9;
    user-select: none;

    &--q {
      color: $msc-faq-q;
    }

    &--a {
      color: $msc-faq-a;
    }
  }

  .msc-faq-item__dot {
    position: absolute;
    right: -0.28em;
    bottom: 0.18em;
    width: 0.28em;
    height: 0.28em;
    border-radius: 50%;
    background: currentColor;
  }

  .msc-faq-item__title {
    font-size: clamp(0.98rem, 2.4vw, 1.1rem);
    font-weight: 700;
    line-height: 1.35;
    color: $msc-faq-ink;
  }

  .msc-faq-item__chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    color: $msc-faq-muted;
    transition: transform 0.2s ease;

    i {
      font-size: 0.85rem;
    }

    .msc-faq-item--open & {
      transform: rotate(180deg);
      color: $msc-faq-q;
    }
  }

  .msc-faq-item__answer {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.75rem 0.9rem;
    align-items: start;
    padding: 0 1rem 1.1rem 0.85rem;
    border-left: 4px solid $msc-faq-a;
    margin-top: -0.15rem;
  }

  .msc-faq-item__body {
    min-width: 0;
    padding-top: 0.35rem;
  }

  .msc-faq-item__text {
    margin: 0;
    font-size: 0.98rem;
    font-weight: 500;
    line-height: 1.6;
    color: $msc-faq-body;
    white-space: pre-line;
  }

  .msc-faq-item__more {
    display: inline-block;
    margin-top: 0.65rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: $msc-faq-q;

    &:hover,
    &:focus {
      color: darken($msc-faq-q, 8%);
    }
  }

  @media (max-width: 575.98px) {
    .msc-faq-item__question,
    .msc-faq-item__answer {
      gap: 0.55rem 0.7rem;
      padding-left: 0.7rem;
      padding-right: 0.75rem;
    }
  }
</style>
